import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import firebase from 'firebase/app';
import { firebaseAuth } from '../config/firebase';
import api, {
  API_ERRORS,
  getApiErrorName,
  getApiErrorStatus,
  setProfileIncompleteHandler,
} from '../services/api';
import { UserGender } from '../utils/enums';
import { useToast } from './toast';
import IRequestError from '../dtos/IRequestError';
import IProfileCompletionStatus from '../dtos/IProfileCompletionStatus';
import ILessonProps from '../dtos/ILessonProps';
import IPlaylistProps from '../dtos/IPlaylistProps';
import IGroupProps from '../dtos/IGroupProps';
import IActivityProps from '../dtos/IActivitiesProps';
import ITrailsProps from '../dtos/ITrailsProps';
import IChapterProps from '../dtos/IChapterProps';
import { ILevelProps } from '../dtos/ILevelProps';

export type SubjectsList = Array<{ subjects_list: string[] }>;

export interface ISchool {
  id: string;
  logo_url: string;
  name: string;
  number_of_kits: number;
}

export interface IClass {
  id: string;
  class_img: string;
  name: string;
  school_id: string;
}

interface ICurrenLessonProps extends ILessonProps {
  group: {
    playlist: IPlaylistProps;
  } & IGroupProps;
}

interface ITrailChapterProps extends IChapterProps {
  trails: ITrailsProps[];
}

interface ICurrentActivityProps extends IActivityProps {
  chapters: ITrailChapterProps[];
}

export interface IUser {
  id: string;
  phone?: string;
  name: string;
  email: string;
  avatar_url: string | null;
  school?: ISchool;
  linkedin: string | null;
  gender: UserGender;
  subjects: string[];
  hardwares: IHardwareProps[];
  subscribed_newsletter: boolean;
  registred_at: string;
  last_access: string | null;
  current_lesson: ICurrenLessonProps;
  current_lesson_id: string | null;
  current_activity: ICurrentActivityProps;
  cookies_policy: boolean;
  privacy_policy: boolean;
  total_points: number;
  current_level: ILevelProps;
  onboarding_completed?: boolean;
  onboarding_skipped?: boolean;
  onboarding_progress?: string;
  onboarding_completed_at?: string;
}

export interface IHardwareProps {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
  trails: ITrailsProps[];
  avatar_url: string;
}

interface IAuthState {
  firebaseUser?: firebase.User;
  /** Só recebe um IUser completo vindo de `GET /user-profile`. Nunca parcial. */
  user?: IUser;
  completionStatus?: IProfileCompletionStatus;
  /**
   * TRÊS ESTADOS, e a distinção importa:
   *   true      → gate satisfeito, app liberado
   *   false     → precisa ir para a tela de conclusão
   *   undefined → DESCONHECIDO (bootstrap falhou por rede/5xx)
   *
   * É o resultado de `isProfileGateSatisfied`. Os guards olham só para este
   * campo — a escola padrão não entra mais na conta.
   *
   * Os guards em routes/PrivateRoute.tsx comparam SEMPRE com `=== true` /
   * `=== false`. Comparar por truthiness faz o estado `undefined` cair no
   * branch de redirect e reintroduz o ping-pong entre as rotas.
   */
  profileCompleted?: boolean;
  /** Falha de rede/5xx no bootstrap — renderiza a página de erro com retry. */
  bootstrapError?: boolean;
}

interface ISignInCredentials {
  email: string;
  password: string;
}

export interface ICompleteProfilePayload {
  school: string;
  privacy_policy: true;
  cookies_policy: true;
  phone?: string | null;
}
interface IUpdatePoints {
  total_points: number;
  current_level: ILevelProps;
}

interface IAuthContextData {
  loading: boolean;
  user: IUser;
  /** Fonte de verdade dos guards: existe sessão Firebase ativa. */
  isAuthenticated: boolean;
  /** Veja o comentário dos três estados em IAuthState. */
  profileCompleted: boolean | undefined;
  completionStatus: IProfileCompletionStatus | undefined;
  /**
   * O usuário está na escola padrão da plataforma e por isso não pode criar
   * torneio. É a única consequência que sobrou da escola padrão.
   */
  onDefaultSchool: boolean;
  bootstrapError: boolean;
  retryBootstrap(): Promise<void>;
  completeProfile(payload: ICompleteProfilePayload): Promise<void>;
  /**
   * Rebusca o `completion-status`. Quem troca de escola pelo perfil precisa
   * disso para o bloqueio de torneio cair na hora, sem recarregar a página.
   */
  refreshCompletionStatus(): Promise<void>;
  refreshIdToken(): Promise<string | null>;
  signIn(credentials: ISignInCredentials): Promise<void>;
  sendMagicLink(email: string): Promise<string>;
  verifyMagicLink(email: string): Promise<void>;
  updateFirebaseUser(firebaseUser: firebase.User): void;
  updateUser(user: IUser): void;
  signOut(): void;
  isSessionExpired(err: IRequestError): Promise<boolean>;
  isOldPasswordCorrect(password: string): Promise<boolean>;
  updatePassword(password: string): Promise<void>;
  updateUserPoints({
    total_points,
    current_level,
  }: IUpdatePoints): Promise<void>;
}

const SESSION_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Fonte única do gate de primeiro login: UMA condição só, o cadastro concluído.
 *
 * Continuar na escola padrão da plataforma (Fuzzy Makers) já bloqueou o app
 * inteiro. Não bloqueia mais — o preço de errar era alto demais: o usuário
 * caía na tela de conclusão sem conseguir sequer cadastrar a escola dele.
 * A restrição sobrevive só na criação de torneio (ver `isOnDefaultSchool`),
 * que é o fluxo em que a escola de fato organiza os dados.
 */
export const isProfileGateSatisfied = (
  status: IProfileCompletionStatus,
): boolean => status.profile_completed;

/**
 * O usuário continua na escola padrão da plataforma e por isso não pode criar
 * torneio. `school_gate_exempt` é a isenção manual de contas internas.
 *
 * Ambos os campos são opcionais de propósito: com um backend antigo eles vêm
 * `undefined`, e nesse caso o app libera — melhor liberar demais do que
 * desabilitar o botão de quem já trocou de escola sem saber por quê.
 */
export const isOnDefaultSchool = (
  status: IProfileCompletionStatus | undefined,
): boolean => !!status?.is_default_school && !status?.school_gate_exempt;

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [authData, setAuthData] = useState<IAuthState>({} as IAuthState);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  /** Derruba a sessão local e a do Firebase. Único caminho de logout do app. */
  const clearSession = useCallback(async () => {
    localStorage.removeItem(`${process.env.NODE_ENV}@Fuzzy:firebaseUser`);
    localStorage.removeItem(`${process.env.NODE_ENV}@Fuzzy:user`);
    localStorage.removeItem(`${process.env.NODE_ENV}@Fuzzy:loginTimestamp`);

    delete api.defaults.headers.authorization;

    setAuthData({} as IAuthState);

    await firebaseAuth.signOut();
  }, []);

  /**
   * Ponto único de entrada autenticado. Começa sempre pelo
   * `completion-status` — rota liberada mesmo com o perfil incompleto, e que
   * ativa a conta silenciosamente no backend — e só busca o perfil completo
   * quando o gate está satisfeito. Sem isso, um usuário com perfil incompleto
   * tomaria 403 no `/user-profile` e cairia em loop de logout.
   *
   * O usuário re-gateado pela escola padrão NÃO tomaria 403 (para o backend o
   * perfil dele está completo), mas o `/user-profile` é pulado do mesmo jeito:
   * a tela de conclusão renderiza fora do MainLayout e não usa o IUser.
   *
   * NUNCA chamar setLoading(true) aqui: o onIdTokenChanged re-dispara a cada
   * refresh silencioso de token (~1h) e um <Loading/> de tela cheia no meio do
   * formulário destruiria o estado do unform. `loading` só vai true → false.
   */
  const bootstrapSession = useCallback(
    async (firebaseUser: firebase.User) => {
      try {
        const token = await firebaseUser.getIdToken(true);

        api.defaults.headers.authorization = `Bearer ${token}`;

        localStorage.setItem(
          `${process.env.NODE_ENV}@Fuzzy:firebaseUser`,
          JSON.stringify(firebaseUser),
        );

        const { data: completionStatus } = await api.get(
          '/user-profile/completion-status',
        );

        if (!isProfileGateSatisfied(completionStatus)) {
          localStorage.removeItem(`${process.env.NODE_ENV}@Fuzzy:user`);

          setAuthData({
            firebaseUser,
            user: undefined,
            completionStatus,
            profileCompleted: false,
          });

          return;
        }

        const { data: profile } = await api.get('/user-profile');

        localStorage.setItem(
          `${process.env.NODE_ENV}@Fuzzy:user`,
          JSON.stringify(profile),
        );

        setAuthData({
          firebaseUser,
          user: profile,
          completionStatus,
          profileCompleted: true,
        });
      } catch (error) {
        const status = getApiErrorStatus(error);
        const name = getApiErrorName(error);

        // Token realmente inválido: o único caso que desloga.
        if (status === 401 || name === API_ERRORS.TOKEN_EXPIRED) {
          addToast({
            type: 'error',
            expression: 'Ops!',
            description:
              'Ocorreu um erro na autenticação. Faça login novamente.',
          });

          await clearSession();
          return;
        }

        // Defensivo: corrida em que o /user-profile bloqueia depois do status.
        if (status === 403 && name === API_ERRORS.PROFILE_INCOMPLETE) {
          setAuthData({ firebaseUser, profileCompleted: false });
          return;
        }

        if (status === 403 && name === API_ERRORS.USER_NOT_ACTIVE) {
          addToast({
            type: 'error',
            expression: 'Conta inativa.',
            description: 'Entre em contato com o suporte.',
          });

          setAuthData({ firebaseUser, profileCompleted: false });
          return;
        }

        // Rede, 5xx, CORS: mantém o usuário logado e oferece nova tentativa.
        setAuthData({
          firebaseUser,
          profileCompleted: undefined,
          bootstrapError: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, clearSession],
  );

  useEffect(() => {
    const unsubscribe = firebaseAuth.onIdTokenChanged(async firebaseUser => {
      if (!firebaseUser) {
        await clearSession();
        setLoading(false);
        return;
      }

      const loginTimestamp = localStorage.getItem(
        `${process.env.NODE_ENV}@Fuzzy:loginTimestamp`,
      );

      if (loginTimestamp) {
        const elapsed = Date.now() - parseInt(loginTimestamp, 10);

        if (elapsed > SESSION_DURATION_MS) {
          await clearSession();
          setLoading(false);
          return;
        }
      } else {
        localStorage.setItem(
          `${process.env.NODE_ENV}@Fuzzy:loginTimestamp`,
          Date.now().toString(),
        );
      }

      await bootstrapSession(firebaseUser);
    });

    return () => unsubscribe();
  }, [bootstrapSession, clearSession]);

  /**
   * Rede de segurança global: qualquer rota que escape das checagens acima e
   * responda 403 user/profile-incomplete devolve o usuário para a tela de
   * conclusão — sem deslogar. 403s concorrentes gravam o mesmo valor, então o
   * React descarta os re-renders repetidos.
   */
  useEffect(() => {
    setProfileIncompleteHandler(() => {
      setAuthData(prev =>
        prev.firebaseUser
          ? { ...prev, user: undefined, profileCompleted: false }
          : prev,
      );
    });

    return () => setProfileIncompleteHandler(null);
  }, []);

  const retryBootstrap = useCallback(async () => {
    const firebaseUser = firebaseAuth.currentUser;

    if (!firebaseUser) {
      return;
    }

    setAuthData(prev => ({ ...prev, bootstrapError: false }));

    await bootstrapSession(firebaseUser);
  }, [bootstrapSession]);

  const refreshIdToken = useCallback(async () => {
    const firebaseUser = firebaseAuth.currentUser;

    if (!firebaseUser) {
      return null;
    }

    try {
      const token = await firebaseUser.getIdToken(true);

      api.defaults.headers.authorization = `Bearer ${token}`;

      return token;
    } catch {
      return null;
    }
  }, []);

  /**
   * Sincroniza o `completionStatus` depois de uma mudança de escola feita fora
   * da tela de conclusão (o formulário do perfil). Silencioso de propósito: é
   * um refinamento de UI, e uma falha aqui só significa que o aviso de torneio
   * bloqueado some no próximo login em vez de agora.
   */
  const refreshCompletionStatus = useCallback(async () => {
    const status: IProfileCompletionStatus | null = await api
      .get('/user-profile/completion-status')
      .then(response => response.data)
      .catch(() => null);

    if (!status) {
      return;
    }

    setAuthData(prev =>
      prev.firebaseUser ? { ...prev, completionStatus: status } : prev,
    );
  }, []);

  /**
   * Conclui o cadastro e hidrata o perfil completo ANTES de retornar. Se a
   * página navegasse para /home com `profileCompleted` ainda false, o
   * PrivateRoute a devolveria para a tela de conclusão.
   */
  const completeProfile = useCallback(
    async (payload: ICompleteProfilePayload) => {
      let completedUser: IUser | null = null;

      try {
        const response = await api.post('/user-profile/complete', payload);

        completedUser = response.data;
      } catch (error) {
        const alreadyCompleted =
          getApiErrorStatus(error) === 409 &&
          getApiErrorName(error) === API_ERRORS.PROFILE_ALREADY_COMPLETED;

        // 409 acontece em duplo clique ou conclusão em outra aba. Do ponto de
        // vista do usuário é sucesso, então segue para a hidratação.
        if (!alreadyCompleted) {
          throw error;
        }
      }

      /*
       * Rebusca o status para o `completionStatus` do contexto não ficar com a
       * escola antiga — é dele que sai o aviso de torneio bloqueado.
       *
       * Falha de rede aqui não pode punir quem concluiu de fato: sem status,
       * segue o caminho otimista e libera.
       */
      const status: IProfileCompletionStatus | null = await api
        .get('/user-profile/completion-status')
        .then(response => response.data)
        .catch(() => null);

      const profile = await api
        .get('/user-profile')
        .then(response => response.data)
        .catch(() => completedUser);

      if (profile) {
        localStorage.setItem(
          `${process.env.NODE_ENV}@Fuzzy:user`,
          JSON.stringify(profile),
        );
      }

      setAuthData(prev => ({
        ...prev,
        user: profile ?? prev.user,
        completionStatus: status ?? prev.completionStatus,
        profileCompleted: true,
        // Cadastro concluído mas sem perfil para hidratar (409 + falha de
        // rede): sinaliza erro para o guard oferecer "tentar novamente", em
        // vez de deixar a tela presa no loading esperando um `user` que
        // nunca vem.
        bootstrapError: !profile && !prev.user,
      }));
    },
    [],
  );

  const signIn = useCallback(
    async ({ email, password }) => {
      const firebaseCredentials = await firebaseAuth
        .signInWithEmailAndPassword(email, password)
        .catch(() => null);

      if (!firebaseCredentials || !firebaseCredentials.user) {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description: 'Email ou senha incorretos. Tente novamente.',
        });

        setAuthData({} as IAuthState);
        return;
      }

      localStorage.setItem(
        `${process.env.NODE_ENV}@Fuzzy:loginTimestamp`,
        Date.now().toString(),
      );

      await bootstrapSession(firebaseCredentials.user);
    },
    [addToast, bootstrapSession],
  );

  const sendMagicLink = useCallback(
    async (email: string) => {
      await api.post('users/login', { email }).catch(error => {
        if (error.response?.status === 404) {
          addToast({
            type: 'error',
            expression: 'Ops!',
            description: 'Email não encontrado. Verifique e tente novamente.',
          });
        }
        addToast({
          type: 'error',
          expression: 'Ops!',
          description: 'Ocorreu um erro ao enviar o link. Tente novamente.',
        });
        throw new Error('magic-link-send-failed');
      });

      localStorage.setItem('fuzzy:magicLinkEmail', email);
      return 'success';
    },
    [addToast],
  );

  const verifyMagicLink = useCallback(
    async (email: string) => {
      const firebaseCredentials = await firebaseAuth
        .signInWithEmailLink(email, window.location.href)
        .catch(() => null);

      if (!firebaseCredentials || !firebaseCredentials.user) {
        setAuthData({} as IAuthState);

        // Só a falha real do link lança: a página de magic link usa isso para
        // mostrar a tela de "link inválido ou expirado".
        throw new Error('magic-link-invalid');
      }

      localStorage.removeItem('fuzzy:magicLinkEmail');
      localStorage.setItem(
        `${process.env.NODE_ENV}@Fuzzy:loginTimestamp`,
        Date.now().toString(),
      );

      // Perfil incompleto NÃO pode lançar daqui, senão a tela de magic link
      // exibiria "Link inválido ou expirado", que é a mensagem errada.
      await bootstrapSession(firebaseCredentials.user);
    },
    [bootstrapSession],
  );

  // Atualizações sempre funcionais: o interceptor de 403 também escreve neste
  // mesmo estado, e um closure velho reverteria `profileCompleted` em silêncio.
  const updateFirebaseUser = useCallback(
    async (firebaseUser: firebase.User) => {
      localStorage.setItem(
        `${process.env.NODE_ENV}@Fuzzy:firebaseUser`,
        JSON.stringify(firebaseUser),
      );

      setAuthData(prev => ({ ...prev, firebaseUser }));
    },
    [],
  );

  const updateUser = useCallback(async (user: IUser) => {
    localStorage.setItem(
      `${process.env.NODE_ENV}@Fuzzy:user`,
      JSON.stringify(user),
    );

    setAuthData(prev => ({ ...prev, user }));
  }, []);

  const signOut = useCallback(async () => {
    // Rota liberada mesmo com o perfil incompleto. Precisa ir antes de limpar
    // o header, e a falha dela nunca pode bloquear o logout local.
    await api.delete('/tokens').catch(() => undefined);

    localStorage.removeItem('notifications');

    await clearSession();
  }, [clearSession]);

  /**
   * Retorna `true` quando o erro JÁ foi tratado aqui e o call site não deve
   * mostrar a própria mensagem. Os call sites usam a forma
   * `if (!(await isSessionExpired(err))) { addToast(...) }`.
   *
   * O `return !tokenExpired` anterior devolvia `true` também no caso normal,
   * o que suprimia TODOS os toasts de erro do app.
   */
  const isSessionExpired = useCallback(
    async (err: IRequestError) => {
      const name = getApiErrorName(err);

      if (getApiErrorStatus(err) === 401 || name === API_ERRORS.TOKEN_EXPIRED) {
        addToast({
          type: 'error',
          expression: 'Sua sessão expirou!',
          description: 'Por favor, faça login novamente.',
        });

        await signOut();
        return true;
      }

      // O interceptor global já está redirecionando para a tela de conclusão:
      // um toast de erro aqui só confundiria o usuário.
      if (name === API_ERRORS.PROFILE_INCOMPLETE) {
        return true;
      }

      return false;
    },
    [addToast, signOut],
  );

  const isOldPasswordCorrect = useCallback(
    async (password: string): Promise<boolean> => {
      const user = firebaseAuth.currentUser;

      if (!user) {
        return false;
      }

      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email as string,
        password,
      );

      const firebaseCredentials = await user
        .reauthenticateWithCredential(credential)
        .catch(() => null);

      if (!firebaseCredentials || !firebaseCredentials.user) {
        return false;
      }

      // const firebaseUser = firebaseCredentials.user;

      // const token = await firebaseUser.getIdToken();

      // api.defaults.headers.authorization = `Bearer ${token}`;

      // setAuthData({ ...authData, firebaseUser });

      return true;
    },
    [],
  );

  const updatePassword = useCallback(
    async (password: string) => {
      const user = firebaseAuth.currentUser;

      if (!user) {
        await signOut();
        return;
      }

      await user.updatePassword(password);
    },
    [signOut],
  );

  const updateUserPoints = useCallback(
    async ({ total_points, current_level }: IUpdatePoints) => {
      setAuthData(prev => {
        if (!prev.user) return prev;

        const updatedUser = { ...prev.user, total_points, current_level };

        localStorage.setItem(
          `${process.env.NODE_ENV}@Fuzzy:user`,
          JSON.stringify(updatedUser),
        );

        return { ...prev, user: updatedUser };
      });
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        loading,
        user: authData.user as IUser,
        isAuthenticated: !!authData.firebaseUser,
        profileCompleted: authData.profileCompleted,
        completionStatus: authData.completionStatus,
        onDefaultSchool: isOnDefaultSchool(authData.completionStatus),
        bootstrapError: !!authData.bootstrapError,
        retryBootstrap,
        completeProfile,
        refreshCompletionStatus,
        refreshIdToken,
        updateUser,
        updateFirebaseUser,
        signIn,
        sendMagicLink,
        verifyMagicLink,
        signOut,
        isSessionExpired,
        isOldPasswordCorrect,
        updatePassword,
        updateUserPoints,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth hook must be used within AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
